// The Worklist's and History's filters in the page's address (`v.*` keys): Back to the page brings
// its view back, a link opens it. Only what differs from the page's starting view is written, and
// the Source whenever it is not All (a shared link shows the same lines); the sort is not — it stays
// the person's, kept in this browser (`saved-sort.ts`). Clear filters is the starting view, so it
// empties the address. Read defensively: an address is typed and shared.
//
// Each page's last view is also kept for this browser tab (`openingView`): its tab in the navigation,
// or a link without a view of its own, opens the page as it was left. Without either, the person's
// saved default, else their customers ("My customers", `assignments.ts`), else the app's
// (`openingViewOf`). My customers is `v.my`: 1 on, 0 taken off — written only for a person who
// has customers, so a link opened by someone else shows theirs.

import { isDay } from "@axny/platform-contracts";
import type { GridView } from "./ask-view";
import { isSourceFilter } from "./deposco";
import type { DateFieldId, DateRange, Mode, TabId, WarehouseGroup } from "./filters";
import {
  HISTORY_DATE_FIELDS,
  HISTORY_TABS,
  WAREHOUSE_GROUPS,
  WORKLIST_DATE_FIELDS,
  WORKLIST_TABS,
  currentPreset,
  presetRange,
} from "./filters";

/** The view as the address keeps it: short keys, only the parts that differ. */
export interface ViewParams {
  src?: string;
  wh?: string[];
  cust?: string[];
  st?: string[];
  kinds?: string[];
  dup?: 1;
  /** The date filter: its field and preset; from and to only for a range picked by hand. */
  date?: { f: string; p: string; from?: string; to?: string };
  col?: Record<string, string[]>;
  tab?: string;
  q?: string;
  /** The Worklist's "Appointment not scheduled" chip. */
  ns?: 1;
  /** My customers: on (1) or taken off (0), for a person who has some. */
  my?: 0 | 1;
  /** The Worklist's "Imported from the workbook" chip (Admin → Import's lines). */
  imp?: 1;
}

type View = Omit<GridView, "sorting">;

const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

/** What of `view` differs from `base` (the page's starting view), or undefined when nothing does. */
export function viewParams(
  view: View,
  base: View,
  notScheduled = false,
  mine?: boolean,
  imported = false,
): ViewParams | undefined {
  const p: ViewParams = {};
  // The Source against All, not the reader's own: an address opened elsewhere shows the same lines.
  if (view.source !== "all") p.src = view.source;
  if (!same(view.warehouses, base.warehouses)) p.wh = [...view.warehouses];
  if (view.customers.length) p.cust = [...view.customers];
  if (view.statuses.length) p.st = [...view.statuses];
  if (!same(view.kinds, base.kinds)) p.kinds = [...view.kinds];
  if (view.duplicates !== base.duplicates) p.dup = 1;
  const r = view.range;
  if (
    r.field !== base.range.field ||
    r.preset !== base.range.preset ||
    r.from !== base.range.from ||
    r.to !== base.range.to
  )
    p.date = {
      f: r.field,
      p: r.preset,
      ...(r.preset === "custom" && r.from ? { from: r.from } : {}),
      ...(r.preset === "custom" && r.to ? { to: r.to } : {}),
    };
  const cols = Object.fromEntries(Object.entries(view.columns).filter(([, v]) => v.length));
  if (Object.keys(cols).length) p.col = cols;
  if (view.tab !== base.tab) p.tab = view.tab;
  if (view.search.trim()) p.q = view.search;
  if (notScheduled) p.ns = 1;
  if (mine !== undefined) p.my = mine ? 1 : 0;
  if (imported) p.imp = 1;
  return Object.keys(p).length ? p : undefined;
}

const strings = (v: unknown, max = 200): string[] | undefined =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, max) : undefined;

/**
 * The view an address names, over the page's starting view: each part read only when it is valid
 * (a tab of this page, a known warehouse, a real date); anything else keeps the starting value.
 */
export function viewOfParams(
  mode: Mode,
  raw: unknown,
  base: GridView,
  today: string,
): { view: GridView; notScheduled: boolean; mine: boolean | undefined; imported: boolean } {
  const p = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const view: GridView = { ...base, columns: { ...base.columns } };
  if (isSourceFilter(p.src)) view.source = p.src;
  const wh = strings(p.wh);
  // An emptied list is kept as one; only unknown warehouses (v.wh=XX) keep the starting ones.
  const known = wh?.filter((w): w is WarehouseGroup => (WAREHOUSE_GROUPS as readonly string[]).includes(w));
  if (wh && known && (known.length || !wh.length)) view.warehouses = known;
  view.customers = strings(p.cust) ?? view.customers;
  view.statuses = strings(p.st) ?? view.statuses;
  if (mode === "history") view.kinds = strings(p.kinds) ?? view.kinds;
  if (p.dup === 1) view.duplicates = !base.duplicates;
  const d = p.date as { f?: unknown; p?: unknown; from?: unknown; to?: unknown } | undefined;
  const fields = (mode === "worklist" ? WORKLIST_DATE_FIELDS : HISTORY_DATE_FIELDS).map((x) => x.id as string);
  if (d && typeof d === "object" && typeof d.f === "string" && fields.includes(d.f) && typeof d.p === "string") {
    const day = (v: unknown) => (typeof v === "string" && isDay(v) ? v : null);
    // A preset counts from today (a retired one as its successor, `currentPreset`); a range picked by hand keeps its days.
    const preset = currentPreset(d.p);
    const range: DateRange =
      preset === "custom"
        ? { field: d.f as DateFieldId, preset: "custom", from: day(d.from), to: day(d.to) }
        : { field: d.f as DateFieldId, preset, ...presetRange(preset, today) };
    view.range = range;
  }
  if (p.col && typeof p.col === "object")
    for (const [id, values] of Object.entries(p.col as Record<string, unknown>)) {
      const v = strings(values);
      if (v?.length && /^[A-Za-z0-9_]{1,40}$/.test(id)) view.columns[id] = v;
    }
  const tabs = (mode === "worklist" ? WORKLIST_TABS : HISTORY_TABS).map((t) => t.id as string);
  if (typeof p.tab === "string" && tabs.includes(p.tab)) view.tab = p.tab as TabId;
  if (typeof p.q === "string") view.search = p.q.slice(0, 200);
  return {
    view,
    notScheduled: mode === "worklist" && p.ns === 1,
    mine: p.my === 1 ? true : p.my === 0 ? false : undefined,
    imported: mode === "worklist" && p.imp === 1,
  };
}

/** The address's prefix for the view's parameters: apart from the page's own (`line`, `tab`). */
const PREFIX = "v.";

/**
 * The view as the router keeps an address — strings, a list as repeated keys (the platform's
 * `stringifySearch`): `v.tab=overdue&v.q=BEALL&v.cust=A&v.cust=B&v.c.division=…&v.date=ptDate~any`.
 */
export function viewToAddress(p: ViewParams | undefined): Record<string, string | string[]> {
  if (!p) return {};
  const out: Record<string, string | string[]> = {};
  const put = (k: string, v: string | string[] | undefined) => {
    if (v !== undefined && (typeof v === "string" ? v : v.length)) out[PREFIX + k] = v;
  };
  put("src", p.src);
  put("wh", p.wh?.length ? p.wh : p.wh ? ["none"] : undefined);
  put("cust", p.cust);
  put("st", p.st);
  put("kinds", p.kinds?.length ? p.kinds : p.kinds ? ["none"] : undefined);
  if (p.dup) put("dup", "1");
  if (p.date) put("date", [p.date.f, p.date.p, p.date.from ?? "", p.date.to ?? ""].join("~").replace(/~+$/, ""));
  for (const [id, values] of Object.entries(p.col ?? {})) put(`c.${id}`, values);
  put("tab", p.tab);
  put("q", p.q);
  if (p.ns) put("ns", "1");
  if (p.my !== undefined) put("my", String(p.my));
  if (p.imp) put("imp", "1");
  return out;
}

/** The view's parameters of an address (`viewToAddress`), for `viewOfParams`; undefined with none. */
export function viewFromAddress(search: Readonly<Record<string, unknown>>): ViewParams | undefined {
  const all = (k: string): string[] | undefined => {
    const v = search[PREFIX + k];
    return typeof v === "string"
      ? [v]
      : Array.isArray(v)
        ? v.filter((x): x is string => typeof x === "string")
        : undefined;
  };
  const one = (k: string) => all(k)?.[0];
  // An emptied list ("none") is kept as one: no warehouse, no kind.
  const list = (k: string) => {
    const v = all(k);
    return v ? v.filter((x) => x !== "none") : undefined;
  };
  const p: ViewParams = {};
  if (one("src")) p.src = one("src");
  if (all("wh")) p.wh = list("wh");
  if (all("cust")) p.cust = all("cust");
  if (all("st")) p.st = all("st");
  if (all("kinds")) p.kinds = list("kinds");
  if (one("dup") === "1") p.dup = 1;
  const date = one("date")?.split("~");
  if (date && date[0] && date[1])
    p.date = { f: date[0], p: date[1], ...(date[2] ? { from: date[2] } : {}), ...(date[3] ? { to: date[3] } : {}) };
  for (const key of Object.keys(search))
    if (key.startsWith(`${PREFIX}c.`))
      (p.col ??= {})[key.slice(PREFIX.length + 2)] = all(key.slice(PREFIX.length)) ?? [];
  if (one("tab")) p.tab = one("tab");
  if (one("q")) p.q = one("q");
  if (one("ns") === "1") p.ns = 1;
  if (one("my") === "1" || one("my") === "0") p.my = Number(one("my")) as 0 | 1;
  if (one("imp") === "1") p.imp = 1;
  return Object.keys(p).length ? p : undefined;
}

/** The address without the view's parameters (the page's own stay). */
export const withoutView = <T extends object>(search: T): T =>
  Object.fromEntries(Object.entries(search).filter(([k]) => !k.startsWith(PREFIX))) as T;

/**
 * The view a page opens with: its address's when it has one (a link, Back), else the one this
 * browser tab last left on the page (`remembered`, its `viewToAddress` form), else none — the
 * starting view. `kept`: opened from the remembered one, and it hides something the starting
 * view would show ("Filters kept from earlier").
 */
export function openingView(
  mode: Mode,
  inAddress: ViewParams | undefined,
  remembered: unknown,
  base: GridView,
  today: string,
): { view: GridView; notScheduled: boolean; imported: boolean; kept: boolean } | null {
  const fromTab =
    !inAddress && remembered && typeof remembered === "object"
      ? viewFromAddress(remembered as Record<string, unknown>)
      : undefined;
  const p = inAddress ?? fromTab;
  if (!p) return null;
  const opened = viewOfParams(mode, p, base, today);
  const key = (v: View, ns = false, imp = false) => JSON.stringify(viewParams(v, base, ns, undefined, imp) ?? null);
  return { ...opened, kept: !inAddress && key(opened.view, opened.notScheduled, opened.imported) !== key(base) };
}

/** Where the view a page opened with came from (`openingViewOf`). */
export type OpenedFrom = "address" | "tab" | "default" | "mine" | "app";

/**
 * The view a page opens with, the first that exists: the address's (a link, Back); the one this
 * browser tab last left (`remembered`, the address form); the person's saved default (`saved`,
 * the same form); their customers (`hasMine`); else the app's (`base`). `kept`: from the tab,
 * and not what a fresh open would show ("Filters kept from earlier"). `mine`: My customers is on.
 */
export function openingViewOf(
  mode: Mode,
  input: { address?: ViewParams; remembered?: unknown; saved?: unknown; hasMine: boolean },
  base: GridView,
  today: string,
): { view: GridView; notScheduled: boolean; imported: boolean; mine: boolean; kept: boolean; from: OpenedFrom } {
  const form = (v: unknown) => (v && typeof v === "object" ? viewFromAddress(v as Record<string, unknown>) : undefined);
  const open = (p: ViewParams, from: OpenedFrom) => {
    const o = viewOfParams(mode, p, base, today);
    return {
      view: o.view,
      notScheduled: o.notScheduled,
      imported: o.imported,
      mine: input.hasMine && !!o.mine,
      kept: false,
      from,
    };
  };
  const saved = form(input.saved);
  const fresh = saved
    ? open(saved, "default")
    : {
        view: base,
        notScheduled: false,
        imported: false,
        mine: input.hasMine,
        kept: false,
        from: (input.hasMine ? "mine" : "app") as OpenedFrom,
      };
  if (input.address) return open(input.address, "address");
  const tab = form(input.remembered);
  if (!tab) return fresh;
  const o = open(tab, "tab");
  const key = (x: typeof o) =>
    JSON.stringify(viewParams(x.view, base, x.notScheduled, input.hasMine ? x.mine : undefined, x.imported) ?? null);
  return { ...o, kept: key(o) !== key(fresh) };
}
